import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";

const APPLIES = [
  ["employee", "Employee"], ["spouse", "Spouse/Partner"], ["child", "Child"], ["other_dependent", "Other dependent"],
];
const EMPLOYMENT = [["full_time", "Full time"], ["part_time", "Part time"]];

// ---------------- Carrier ----------------
const EMPTY_CARRIER = { name: "", code: "", am_best_rating: "", website: "", logo_url: "", notes: "" };

export function CarrierDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_CARRIER);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) {
      const clean = initial
        ? Object.fromEntries(Object.entries({ ...EMPTY_CARRIER, ...initial }).map(([k, v]) => [k, v ?? ""]))
        : EMPTY_CARRIER;
      setForm(clean);
    }
  }, [open, initial]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, code: form.code, am_best_rating: form.am_best_rating || null,
        website: form.website || null, logo_url: form.logo_url || null, notes: form.notes || null };
      if (editing) await api.put(`/admin/carriers/${initial.id}`, payload);
      else await api.post("/admin/carriers", payload);
      toast.success(editing ? "Carrier updated" : "Carrier created");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto" data-testid="carrier-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit carrier" : "Add carrier"}</DialogTitle>
          <DialogDescription>Carriers group the products offered on the platform.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold">Name</Label>
              <Input required value={form.name} onChange={upd("name")} className="mt-1.5" data-testid="carrier-name" />
            </div>
            <div>
              <Label className="font-semibold">Code</Label>
              <Input required value={form.code} onChange={upd("code")} className="mt-1.5" data-testid="carrier-code" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold">AM Best rating</Label>
              <Input value={form.am_best_rating} onChange={upd("am_best_rating")} className="mt-1.5" data-testid="carrier-rating" />
            </div>
            <div>
              <Label className="font-semibold">Website</Label>
              <Input value={form.website} onChange={upd("website")} className="mt-1.5" placeholder="https://" data-testid="carrier-website" />
            </div>
          </div>
          <div>
            <Label className="font-semibold">Notes</Label>
            <Textarea value={form.notes} onChange={upd("notes")} className="mt-1.5" rows={2} data-testid="carrier-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-gold text-navy-900 hover:bg-gold-light font-bold" data-testid="save-carrier">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Create carrier"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Product ----------------
const EMPTY_PRODUCT = {
  name: "", carrier_id: "", benefit_category_key: "", product_type: "", version: "1.0",
  policy_form: "", summary: "", plain_language: "", benefit_limits: "",
  statesStr: "", ridersStr: "", is_demo: true,
  el_min: "", el_max: "", el_applies: ["employee"], el_employment: ["full_time"], el_notes: "",
  options: [{ tier: "employee", label: "Employee only", monthly_premium: "", coverage_amount: "" }],
};

function productToForm(p) {
  const el = p.eligibility || {};
  return {
    name: p.name || "", carrier_id: p.carrier_id || "", benefit_category_key: p.benefit_category_key || "",
    product_type: p.product_type || "", version: p.version || "1.0", policy_form: p.policy_form || "",
    summary: p.summary || "", plain_language: p.plain_language || "", benefit_limits: p.benefit_limits || "",
    statesStr: (p.state_availability || []).join(", "), ridersStr: (p.riders || []).join(", "),
    is_demo: p.is_demo !== false,
    el_min: el.min_age ?? "", el_max: el.max_age ?? "",
    el_applies: el.applies_to || ["employee"], el_employment: el.employment_type || ["full_time"],
    el_notes: el.notes || "",
    options: (p.coverage_options && p.coverage_options.length ? p.coverage_options : EMPTY_PRODUCT.options).map((o) => ({
      tier: o.tier || "", label: o.label || "",
      monthly_premium: o.monthly_premium ?? "", coverage_amount: o.coverage_amount ?? "",
    })),
  };
}

export function ProductDialog({ open, onOpenChange, initial, carriers, categories, onSaved }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) setForm(initial ? productToForm(initial) : EMPTY_PRODUCT);
  }, [open, initial]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleArr = (k, val) => {
    const arr = form[k].includes(val) ? form[k].filter((x) => x !== val) : [...form[k], val];
    setForm({ ...form, [k]: arr });
  };
  const setOpt = (i, k, v) => {
    const options = form.options.map((o, idx) => (idx === i ? { ...o, [k]: v } : o));
    setForm({ ...form, options });
  };
  const addOpt = () => setForm({ ...form, options: [...form.options, { tier: "", label: "", monthly_premium: "", coverage_amount: "" }] });
  const removeOpt = (i) => setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    if (!form.carrier_id || !form.benefit_category_key) {
      toast.error("Choose a carrier and a benefit category.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        carrier_id: form.carrier_id, benefit_category_key: form.benefit_category_key,
        name: form.name, product_type: form.product_type, version: form.version || "1.0",
        policy_form: form.policy_form || null, summary: form.summary || null,
        plain_language: form.plain_language || null, benefit_limits: form.benefit_limits || null,
        state_availability: form.statesStr.split(",").map((s) => s.trim()).filter(Boolean),
        riders: form.ridersStr.split(",").map((s) => s.trim()).filter(Boolean),
        is_demo: !!form.is_demo,
        eligibility: {
          min_age: form.el_min === "" ? null : parseInt(form.el_min),
          max_age: form.el_max === "" ? null : parseInt(form.el_max),
          states: [], applies_to: form.el_applies, employment_type: form.el_employment,
          notes: form.el_notes || null,
        },
        coverage_options: form.options
          .filter((o) => o.label || o.tier)
          .map((o) => ({
            tier: o.tier || "employee", label: o.label || o.tier,
            monthly_premium: o.monthly_premium === "" ? null : parseFloat(o.monthly_premium),
            coverage_amount: o.coverage_amount === "" ? null : parseFloat(o.coverage_amount),
          })),
      };
      if (editing) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit product" : "Add product"}</DialogTitle>
          <DialogDescription>Define the product, its coverage options, and eligibility rules. Structured data — ready for real carrier materials.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold">Product name</Label>
              <Input required value={form.name} onChange={upd("name")} className="mt-1.5" data-testid="product-name" />
            </div>
            <div>
              <Label className="font-semibold">Product type</Label>
              <Input required value={form.product_type} onChange={upd("product_type")} className="mt-1.5" placeholder="e.g. Dental PPO" data-testid="product-type" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold">Carrier</Label>
              <Select value={form.carrier_id} onValueChange={(v) => setForm({ ...form, carrier_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="product-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>
                  {carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold">Benefit category</Label>
              <Select value={form.benefit_category_key} onValueChange={(v) => setForm({ ...form, benefit_category_key: v })}>
                <SelectTrigger className="mt-1.5" data-testid="product-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.key}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold">Version</Label>
              <Input value={form.version} onChange={upd("version")} className="mt-1.5" data-testid="product-version" />
            </div>
            <div>
              <Label className="font-semibold">Policy form</Label>
              <Input value={form.policy_form} onChange={upd("policy_form")} className="mt-1.5" data-testid="product-policy-form" />
            </div>
          </div>
          <div>
            <Label className="font-semibold">Plain-language description</Label>
            <Textarea value={form.plain_language} onChange={upd("plain_language")} className="mt-1.5" rows={2} placeholder="What this benefit does, in simple terms." data-testid="product-plain" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold">Benefit limits</Label>
              <Input value={form.benefit_limits} onChange={upd("benefit_limits")} className="mt-1.5" data-testid="product-limits" />
            </div>
            <div>
              <Label className="font-semibold">State availability</Label>
              <Input value={form.statesStr} onChange={upd("statesStr")} className="mt-1.5" placeholder="TX, CA, NY (comma separated)" data-testid="product-states" />
            </div>
          </div>
          <div>
            <Label className="font-semibold">Riders / features</Label>
            <Input value={form.ridersStr} onChange={upd("ridersStr")} className="mt-1.5" placeholder="Telehealth, Wellness benefit (comma separated)" data-testid="product-riders" />
          </div>

          {/* Eligibility */}
          <div className="rounded-xl border border-mist-200 p-4">
            <p className="font-display text-lg font-bold text-navy-900 mb-3">Eligibility rules</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="font-semibold">Min age</Label>
                <Input type="number" value={form.el_min} onChange={upd("el_min")} className="mt-1.5" data-testid="product-min-age" />
              </div>
              <div>
                <Label className="font-semibold">Max age</Label>
                <Input type="number" value={form.el_max} onChange={upd("el_max")} className="mt-1.5" data-testid="product-max-age" />
              </div>
            </div>
            <Label className="font-semibold">Applies to</Label>
            <div className="flex flex-wrap gap-4 mt-2 mb-4">
              {APPLIES.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`applies-${k}`}>
                  <Checkbox checked={form.el_applies.includes(k)} onCheckedChange={() => toggleArr("el_applies", k)} /> {label}
                </label>
              ))}
            </div>
            <Label className="font-semibold">Employment type</Label>
            <div className="flex flex-wrap gap-4 mt-2">
              {EMPLOYMENT.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`employment-${k}`}>
                  <Checkbox checked={form.el_employment.includes(k)} onCheckedChange={() => toggleArr("el_employment", k)} /> {label}
                </label>
              ))}
            </div>
          </div>

          {/* Coverage options */}
          <div className="rounded-xl border border-mist-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display text-lg font-bold text-navy-900">Coverage options</p>
              <Button type="button" variant="outline" size="sm" onClick={addOpt} className="rounded-full" data-testid="add-option">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-3">
              {form.options.map((o, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`option-row-${i}`}>
                  <div className="col-span-3">
                    <Label className="text-xs">Tier</Label>
                    <Input value={o.tier} onChange={(e) => setOpt(i, "tier", e.target.value)} className="mt-1 h-9" placeholder="employee" data-testid={`option-tier-${i}`} />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Label</Label>
                    <Input value={o.label} onChange={(e) => setOpt(i, "label", e.target.value)} className="mt-1 h-9" placeholder="Employee only" data-testid={`option-label-${i}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">$/mo</Label>
                    <Input type="number" step="0.01" value={o.monthly_premium} onChange={(e) => setOpt(i, "monthly_premium", e.target.value)} className="mt-1 h-9" data-testid={`option-premium-${i}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Benefit $</Label>
                    <Input type="number" value={o.coverage_amount} onChange={(e) => setOpt(i, "coverage_amount", e.target.value)} className="mt-1 h-9" data-testid={`option-amount-${i}`} />
                  </div>
                  <div className="col-span-1">
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeOpt(i)} data-testid={`remove-option-${i}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-gold text-navy-900 hover:bg-gold-light font-bold" data-testid="save-product">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Create product"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
